import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, SendIcon, XIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { emailTemplates } from '../../data/configuration';
import type { Lead } from '../../types';
interface EmailPanelProps {
  open: boolean;
  lead: Lead;
  onClose: () => void;
  onSend: (templateName: string) => void;
}
export function EmailPanel({
  open,
  lead,
  onClose,
  onSend
}: EmailPanelProps) {
  const [templateId, setTemplateId] = useState(emailTemplates[0].id);
  const template = emailTemplates.find(t => t.id === templateId) ?? emailTemplates[0];
  const firstName = lead.name.split(' ')[0];
  const rendered = template.body.replace(/{{FirstName}}/g, firstName).replace(/{{OwnerName}}/g, 'Sarah Johnson');
  return <AnimatePresence data-id="element-171" data-mp="2eejl5b16cpp6">
      {open ? <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Send email" data-id="element-172" data-mp="2efkxrjxu9k90">
          <motion.button type="button" aria-label="Close email panel" className="absolute inset-0 bg-ink-900/25" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-173" data-mp="29edpljrsaj1t" />
          <motion.div initial={{
        x: 48,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 48,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 34
      }} className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-lift" data-id="element-174" data-mp="3hjcynai01c2n">
            <div className="flex items-start justify-between border-b border-ink-200 px-6 py-5" data-id="element-175" data-mp="41ki9vr4zlte">
              <div data-id="element-176" data-mp="91qabd1bjac0">
                <h2 className="text-base font-semibold text-ink-900" data-id="element-177" data-mp="905hpsyke1gs">Send Email</h2>
                <p className="mt-0.5 text-sm text-ink-500" data-id="element-178" data-mp="e12yq27u8pgp">
                  To {lead.name} · {lead.email}
                </p>
              </div>
              <button type="button" onClick={onClose} className="rounded-lg p-2 text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-800" aria-label="Close" data-id="element-179" data-mp="hzskai315dbf">
                <XIcon className="h-4 w-4" data-id="element-180" data-mp="2nvcp0hqhru00" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-181" data-mp="2j4cufj4uho0o">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-500" data-id="element-182" data-mp="2j204ojsui9d2">
                Salesforce Email Template
              </p>
              <div className="mt-3 space-y-2" data-id="element-183" data-mp="2supe794u5d5r">
                {emailTemplates.map(t => {
              const active = t.id === templateId;
              return <button key={t.id} type="button" onClick={() => setTemplateId(t.id)} className={twMerge('flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors', active ? 'border-brand-300 bg-brand-50' : 'border-ink-200 bg-white hover:border-ink-300')} data-id="element-184" data-mp="32s3rizvgpltu">
                      <span data-id="element-185" data-mp="oj9pjln8hag4">
                        <span className="block text-sm font-medium text-ink-900" data-id="element-186" data-mp="ok2nbx47spm4">{t.name}</span>
                        <span className="mt-0.5 block text-xs text-ink-500" data-id="element-187" data-mp="thkylu5pfgxa">{t.subject}</span>
                      </span>
                      {active ? <CheckIcon className="h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" data-id="element-188" data-mp="jlhxmrml9obx" /> : null}
                    </button>;
            })}
              </div>

              <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500" data-id="element-189" data-mp="all1y61s7p7d">
                Preview
              </p>
              <div className="mt-3 rounded-xl border border-ink-200 bg-ink-50 p-5" data-id="element-190" data-mp="1zxetwfrczmbz">
                <p className="text-sm font-medium text-ink-900" data-id="element-191" data-mp="24y2m34rbq97s">{template.subject}</p>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-600" data-id="element-192" data-mp="4v0cr17rejgq">
                  {rendered}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-ink-200 px-6 py-4" data-id="element-193" data-mp="3r2rtf1dtqght">
              <button type="button" onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-medium text-ink-600 transition-colors hover:bg-ink-100" data-id="element-194" data-mp="3n2pswvjtrt44">
                Cancel
              </button>
              <button type="button" onClick={() => onSend(template.name)} className="flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-195" data-mp="38d1q1pvzwmea">
                <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-196" data-mp="2zegc681gxvju" />
                Send Email
              </button>
            </div>
          </motion.div>
        </div> : null}
    </AnimatePresence>;
}